import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbDatepickerModule, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { DlgWalrusModule } from 'dlg-walrus';

import { CardTypeComponent } from './credit-card/card-type/card-type.component';
import { CardholderThirdPartyComponent } from './credit-card/cardholder-third-party/cardholder-third-party.component';
import { DetailsDisplayComponent } from './details-display/details-display.component';
import { ModalBreakdownComponent } from './modals/breakdown-modal/breakdown-modal.component';
import { SingleAlertComponent } from './single-alert/single-alert.component';
import { SessionWarningModalComponent } from './modals/session-warning-modal/session-warning-modal.component';
import { DowngradeModalComponent } from './modals/downgrade-modal/downgrade-modal.component';
import { ModalNcdComponent } from './modals/ncd-modal/ncd-modal.component';
import { BenefitsModalComponent } from './modals/benefits-modal/benefits-modal.component';
import { ConfirmRemoveModalComponent } from './modals/confirm-remove-modal/confirm-remove-modal.component';
import { CoverTypesComponent } from './manage-cover-types/cover-types/cover-types.component';
import { CoverInfoComponent } from './manage-cover-types/cover-info/cover-info.component';
import { CoverTypeBenefitsListComponent } from './manage-cover-types/cover-type-benefits-list/cover-type-benefits-list.component';
import { DrivingHistoryComponent } from './driving-history/driving-history.component';
import { DriverClaimsComponent } from './driver-claims/driver-claims.component';
import { ManageClaimComponent } from './driver-claims/manage-claim/manage-claim.component';
import { ClaimConvictionListItemComponent } from './claim-conviction-list-item/claim-conviction-list-item.component';
import { AddressOvernightComponent } from './address-overnight/address-overnight.component';
import { CarSelectManualComponent } from './car-select-manual/car-select-manual.component';
import { CarModificationsComponent } from './car-modifications/car-modifications.component';
import { PanelComponent } from './car-modifications/panel/panel.component';
import { PanelItemComponent } from './car-modifications/panel/panel-item/panel-item.component';
import { PaymentContactCentreComponent } from './payment-contact-centre/payment-contact-centre.component';
import { CarRegLookupComponent } from './car-reg-lookup/car-reg-lookup.component';
import { CarLookupComponent } from './car-lookup/car-lookup.component';
import { BreakdownDetailsComponent } from './breakdown-details/breakdown-details.component';
import { RegistrationPanelComponent } from './registration-panel/registration-panel.component';
import { AlertComponent } from './components/alert/alert.component';
import { DatepickerComponent } from './components/datepicker/date-picker.component';

import { FormatAmountPipe } from '@global/pipes/amount-format.pipe';
import { TitleCaseDirective } from '@global/directives/title-case.directive';
import { SessionInterceptor } from '@global/services/session/session.interceptor';

import { GlobalErrorHandler } from '@global/errors/global-error-handler';
import { DateInputFormatterDirective } from './components/datepicker/date-input-formatter.directive';
import { AdditionalDetailsComponent } from './additional-details/additional-details.component';
import { MileageDisplayComponent } from './mileage-display/mileage-display.component';
import { CarPanelComponent } from './car-panel/car-panel.component';
import { CarStatusReminderComponent } from './car-status-reminder/car-status-reminder.component';
import { GetAppCtaComponent } from './get-app-cta/get-app-cta.component';
import { TabListComponent } from './tabs/tab-list/tab-list.component';
import { TabListItemComponent } from './tabs/tab-list-item/tab-list-item.component';
import { ContactUsComponent } from './contact-us/contact-us.component';
import { DriverConvictionsComponent } from './driver-convictions/driver-convictions.component';
import { ManageConvictionComponent } from './driver-convictions/manage-conviction/manage-conviction.component';
import { SvgIconComponent } from './components/svg-icon/svg-icon.component';
import { CardSaveConsentComponent } from './credit-card/card-save-consent/card-save-consent.component';
import { CreditCardListTableComponent } from './credit-card/credit-card-list-table/credit-card-list-table.component';
import { AlertSupportPanelComponent } from './components/alert-support-panel/alert-support-panel.component';
import { LoadingAnimation } from './loading-animation/loading-animation.component';

@NgModule({
  declarations: [
    CardTypeComponent,
    CardholderThirdPartyComponent,
    DetailsDisplayComponent,
    ModalBreakdownComponent,
    SessionWarningModalComponent,
    DowngradeModalComponent,
    ModalNcdComponent,
    SingleAlertComponent,
    DatepickerComponent,
    FormatAmountPipe,
    TitleCaseDirective,
    CoverTypesComponent,
    BenefitsModalComponent,
    CoverInfoComponent,
    CoverTypeBenefitsListComponent,
    DrivingHistoryComponent,
    DriverClaimsComponent,
    ManageClaimComponent,
    DriverConvictionsComponent,
    ManageConvictionComponent,
    ConfirmRemoveModalComponent,
    ClaimConvictionListItemComponent,
    CarSelectManualComponent,
    AddressOvernightComponent,
    CarModificationsComponent,
    PaymentContactCentreComponent,
    CarRegLookupComponent,
    CarLookupComponent,
    CarPanelComponent,
    CarStatusReminderComponent,
    PanelComponent,
    PanelItemComponent,
    TabListComponent,
    TabListItemComponent,
    BreakdownDetailsComponent,
    AdditionalDetailsComponent,
    RegistrationPanelComponent,
    MileageDisplayComponent,
    GetAppCtaComponent,
    ContactUsComponent,
    CardSaveConsentComponent,
    CreditCardListTableComponent,
    DateInputFormatterDirective,
    LoadingAnimation,
    AlertComponent,
    SvgIconComponent,
    AlertSupportPanelComponent
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NgbModule,
    NgbDatepickerModule,
    DlgWalrusModule,
  ],
  exports: [
    CommonModule,
    CardTypeComponent,
    CardholderThirdPartyComponent,
    DetailsDisplayComponent,
    ModalBreakdownComponent,
    SessionWarningModalComponent,
    DowngradeModalComponent,
    CoverTypesComponent,
    BenefitsModalComponent,
    ModalNcdComponent,
    SingleAlertComponent,
    DatepickerComponent,
    DrivingHistoryComponent,
    DriverClaimsComponent,
    ManageClaimComponent,
    DriverConvictionsComponent,
    ManageConvictionComponent,
    ConfirmRemoveModalComponent,
    ClaimConvictionListItemComponent,
    AddressOvernightComponent,
    CarSelectManualComponent,
    CarModificationsComponent,
    CarStatusReminderComponent,
    PanelComponent,
    PanelItemComponent,
    TabListComponent,
    TabListItemComponent,
    PaymentContactCentreComponent,
    CarRegLookupComponent,
    CarLookupComponent,
    CarPanelComponent,
    BreakdownDetailsComponent,
    AdditionalDetailsComponent,
    RegistrationPanelComponent,
    MileageDisplayComponent,
    GetAppCtaComponent,
    ContactUsComponent,
    CardSaveConsentComponent,
    CreditCardListTableComponent,
    AlertComponent,
    LoadingAnimation,
    SvgIconComponent,
    FormatAmountPipe,
    TitleCaseDirective,
    AlertSupportPanelComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: SessionInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
})
export class SharedFeaturesModule {}
